// Centralized default values for NewQuote state

export const UI_DEFAULTS = {
	isRotatedArrow: false,
	isRotatedBox: false,
	isReversed: false,
	orientation: 'left-to-right',
	isAddingNew: false,
	items: ['Infeed', 'Material Flow', 'Outfeed']
};

export const MODAL_DEFAULTS = {
	customModalOpen: false,
	showQuoteDetailsModal: false,
	showLengthLimitModal: false,
	showSpacerModal: false,
	lengthLimitMessage: ''
};

export const LIST_DEFAULTS = {
	customers: [],
	saws: [],
	conveyorStyles: [],
	dropdownOptions: {
		rollerLengths: {
			EX: ['48"', '58"', '70"', '82"'],
			HD: ['24"', '30"', '36"', '42"', '48"', '60"', '72"'],
			SG: ['24"', '30"', '36"', '42"', '48"', '60"', '72"'],
			DEFAULT: ['24"', '30"', '36"', '42"', '48"', '60"', '72"']
		},
		conveyorStyles: []
	}
};

export const SELECTION_DEFAULTS = {
	selectedSaw: null,
	selectedCustomer: null,
	selectedCustomerName: '',
	selectedRollerLength: '',
	leftBoxRollerLength: '',
	rightBoxRollerLength: '',
	selectedConveyorStyle: '',
	leftBoxConveyorStyle: '',
	rightBoxConveyorStyle: '',
	additionalEmails: [],
	selectedAdditionalEmails: [],
	selectedContact: null
};

export const BOX_DEFAULTS = {
	leftBoxItems: [],
	rightBoxItems: [],
	activeBox: 'left',
	checkedItems: {
		left: new Map(),
		right: new Map()
	},
	flippedBoxes: { left: false, right: false },
	filenameFlipStates: { left: false, right: false },
	motorSelections: { left: null, right: null },
	taSelections: { left: {}, right: {} },
	stkSelections: { left: {}, right: {} },
	leftBoxCacheClear: 0,
	rightBoxCacheClear: 0
};

export const CACHE_DEFAULTS = {
	globalImageCache: new Map()
};

export const LENGTHS_BUTTONS_DEFAULTS = {
	infeedLength: '',
	outfeedLength: '',
	buttonsEnabled: { tenFt: false, tenFtFork: false, fiveFt: false, liftTable: false, spacer: false }
};

export const FORM_DEFAULTS = {
	formFields: { datumSide: '', workBedHeight: '', sawType: '', quotePrice: '', customerRFQ: '', standard_discount: '', price: '' }
};

export const QUOTE_DEFAULTS = {
	isQuoteCreated: false,
	quoteNumber: '',
	// Per-quote brochure filename (in babs-images/brochures/). Independent of the saw's
	// own brochure; empty means no brochure is attached to this quote.
	brochure: '',
	quoteDetails: {
		color: '',
		sawDelivery: '',
		conveyorDelivery: '',
		deliveryTerms: '',
		inspection: '',
		warranty: '',
		tax: '',
		payment: '',
		location: '',
		installation: {
			techs: '0',
			days: '0',
			laborTotal: '',
			installationTotal: '',
			showDetails: false,
			deliveryMethod: '',
			installationOption: '',
			installQuoted: ''
		}
	}
};

export const DATA_DEFAULTS = {
	sampleData: []
};

// TA variant options (like STK 5FT/10FT): 5FT, 10FT, 15, 20, and special
export const TA_VARIANTS = ['5FT', '10FT', '15', '20', 'SPECIAL'];

const emptyTAImageSlot = () => ({ up: null, down: null, up10FT: null, down10FT: null, up15: null, down15: null, up20: null, down20: null, upSpecial: null, downSpecial: null });

export const IMAGE_DEFAULTS = {
	motorVariantImageUrls: { left: { motor: null, motor1: null, motor2: null }, right: { motor: null, motor1: null, motor2: null } },
	taImageUrls: { left: emptyTAImageSlot(), right: emptyTAImageSlot() },
	stkImageUrls: { left: { high: null, low: null, high10FT: null, low10FT: null, high15FT: null, low15FT: null, high20FT: null, low20FT: null }, right: { high: null, low: null, high10FT: null, low10FT: null, high15FT: null, low15FT: null, high20FT: null, low20FT: null } },
	motorVariantDetails: { left: { motor: null, motor1: null, motor2: null }, right: { motor: null, motor1: null, motor2: null } },
	taDetails: { left: emptyTAImageSlot(), right: emptyTAImageSlot() },
	stkDetails: { left: { high: null, low: null, high10FT: null, low10FT: null, high15FT: null, low15FT: null, high20FT: null, low20FT: null }, right: { high: null, low: null, high10FT: null, low10FT: null, high15FT: null, low15FT: null, high20FT: null, low20FT: null } }
};

export const MOTOR_DEFAULTS = {
	motorVariantByBox: { left: '', right: '' },
	motorDetailsByBox: { left: null, right: null },
	isFlipped: false,
	usedRefreshOnce: { left: false, right: false }
};

export const STK_DEFAULTS = {
	stkVariantByPortion: { left: {}, right: {} }
};

export const TA_DEFAULTS = {
	taVariantByPortion: { left: {}, right: {} }
};

export const MEDIA_DEFAULTS = {
	sawImage: null
};

// Reusable constant maps and arrays for New Quote
export const MODEL_MAP = {
	'5FT': '5FT',
	'10FT': '10FT',
	'10FTFORK': '10FTFORK',
	'LFT': 'LTOPTION'
};

// Angled Table identifiers
export const ATBL_ALT = 'ATBL';
export const ATBL_PART_PREFIX = 'BABS-8-ATBL-30-';

// Classify an ATBL doc into one of the four pair slots based on its part_number suffix.
// Returns 'leftDefault' | 'leftFlipped' | 'rightDefault' | 'rightFlipped' | null.
// Order matters: check the 'D' (flipped) variants before the bare 'IF'/'OF' so that
// e.g. an 'IFD' suffix is not matched by the 'IF' branch.
export const classifyATBLDoc = (doc) => {
	const pn = (doc?.part_number || '').toUpperCase();
	if (!pn.startsWith(ATBL_PART_PREFIX)) return null;
	const suffix = pn.slice(ATBL_PART_PREFIX.length);
	if (suffix.includes('IFD')) return 'leftFlipped';
	if (suffix.includes('OFD')) return 'rightFlipped';
	if (suffix.includes('IF')) return 'leftDefault';
	if (suffix.includes('OF')) return 'rightDefault';
	return null;
};

// Extract the four ATBL docs from a list of modular_conveyors docs, keyed by slot.
export const extractATBLDocs = (conveyors = []) => {
	const out = { leftDefault: null, leftFlipped: null, rightDefault: null, rightFlipped: null };
	conveyors.forEach((doc) => {
		const slot = classifyATBLDoc(doc);
		if (slot && !out[slot]) out[slot] = doc;
	});
	return out;
};

// Pick the doc that should be shown for a given side and flip state.
export const pickATBLDoc = (atblDocs, boxName, isFlipped) => {
	if (!atblDocs) return null;
	if (boxName === 'left') return isFlipped ? atblDocs.leftFlipped : atblDocs.leftDefault;
	return isFlipped ? atblDocs.rightFlipped : atblDocs.rightDefault;
};

// Derive per-module option toggle state (VR / CP / PW / MT / STK / TA) from a
// persisted module document. Source-of-truth ordering matters: VR/CP/MT/PW
// were historically dropped or partially persisted in module_options[0], so
// the filename token and the dedicated *_yn field are checked first; STK/TA
// are read straight from module_options[0]. The mt variant string falls back
// to motor_hp_value when module_options[0].mt is missing.
export const deriveModuleOptionStates = (module) => {
	const empty = { vr: false, cp: false, pw: false, mt: false, stk: false, ta: false, mtVariant: '', stkVariant: '', taVariant: '' };
	if (!module) return empty;
	const filename = String(module.filename || '').toUpperCase();
	// Strip the negative tokens so a bare VR/CP/PW substring means "on".
	const fnStripped = filename.replace(/NVR/g, '').replace(/NCP/g, '').replace(/NPW/g, '');
	const options = (module.module_options && module.module_options[0]) || {};
	const yn = (v) => String(v || '').toUpperCase() === 'YES';

	let mtVariant = typeof options.mt === 'string' ? options.mt : '';
	if (!mtVariant && yn(module.motor_yn)) {
		const hp = String(module.motor_hp_value || '');
		if (hp === '5HP') mtVariant = 'motor';
		else if (hp === '3HP') mtVariant = 'motor1';
		else if (hp === '7.5HP') mtVariant = 'motor2';
	}

	return {
		vr: yn(module.vertical_roller_yn) || fnStripped.includes('VR') || !!options.vr,
		cp: yn(module.coolant_pan_yn) || fnStripped.includes('CP') || !!options.cp,
		pw: !!options.pw || fnStripped.includes('PW'),
		mt: yn(module.motor_yn) || !!options.mt,
		stk: !!options.stk,
		ta: !!options.ta,
		mtVariant,
		stkVariant: options.stkVariant || '',
		taVariant: options.taVariant || '',
	};
};

export const DEFAULT_MODEL = '5FT';

export const CONVEYOR_LABELS = ['PW', 'VR', 'CP'];

export const LABEL_MAPPING = { PW: 'PW', VR: 'VR', CP: 'CP' };

export const SPECIAL_LABELS = ['TA', 'STK', 'MT'];

export const ALL_ITEM_LABELS = ['PW', 'VR', 'MT', 'CP', 'STK', 'TA'];

// Per-box cap: infeed and outfeed each get this budget independently, not shared.
export const LENGTH_LIMIT_MAX_FT = 90;

export const DEFAULTS = {
	ui: UI_DEFAULTS,
	modals: MODAL_DEFAULTS,
	lists: LIST_DEFAULTS,
	selections: SELECTION_DEFAULTS,
	boxes: BOX_DEFAULTS,
	cache: CACHE_DEFAULTS,
	lengthsAndButtons: LENGTHS_BUTTONS_DEFAULTS,
	form: FORM_DEFAULTS,
	quote: QUOTE_DEFAULTS,
	data: DATA_DEFAULTS,
	images: IMAGE_DEFAULTS,
	motors: MOTOR_DEFAULTS,
	stk: STK_DEFAULTS,
	ta: TA_DEFAULTS,
	media: MEDIA_DEFAULTS
};

export default DEFAULTS;


